/**
 * @module useSimulation
 */
import { useCallback } from 'react'
import {
  useCreateSimulationMutation,
  useGetSimulationMutation,
  useUpdateSimulationMutation,
  useDeleteSimulationMutation,
  useStartSimulationMutation,
  useStopSimulationMutation,
  useGetActiveSimulationsMutation,
  useGetArchivedSimulationsMutation,
} from '../services/simulation.service'

export const useSimulation = () => {
  const [executeCreateSimulation, { isLoading: isCreateSimulationLoading }] =
    useCreateSimulationMutation()
  const [executeGetSimulation, { isLoading: isGetSimulationLoading }] = useGetSimulationMutation()
  const [executeUpdateSimulation, { isLoading: isUpdateSimulationLoading }] =
    useUpdateSimulationMutation()
  const [executeDeleteSimulation, { isLoading: isDeleteSimulationLoading }] =
    useDeleteSimulationMutation()
  const [executeStartSimulation, { isLoading: isStartSimulationLoading }] =
    useStartSimulationMutation()
  const [executeStopSimulation, { isLoading: isStopSimulationLoading }] =
    useStopSimulationMutation()
  const [executeGetActiveSimulations, { isLoading: isGetActiveSimulationsLoading }] =
    useGetActiveSimulationsMutation()
  const [executeGetArchivedSimulations, { isLoading: isGetArchivedSimulationsLoading }] =
    useGetArchivedSimulationsMutation()

  const createSimulationAsync = useCallback(
    async (simulationData) => {
      if (isCreateSimulationLoading) {
        return
      }
      try {
        const response = await executeCreateSimulation(simulationData).unwrap()
        return response.data.simulation
      } catch (error) {
        console.error('#createSimulationAsync: Error creating simulation', error)
        throw error
      }
    },
    [executeCreateSimulation, isCreateSimulationLoading],
  )

  const getSimulationAsync = useCallback(
    async (simulationNumber) => {
      if (isGetSimulationLoading) {
        return
      }
      try {
        const response = await executeGetSimulation(simulationNumber).unwrap()
        return response.data.simulation
      } catch (error) {
        console.error('#getSimulationAsync: Error fetching simulation', error)
        throw error
      }
    },
    [executeGetSimulation, isGetSimulationLoading],
  )

  const updateSimulationAsync = useCallback(
    async (simulationNumber, updates) => {
      if (isUpdateSimulationLoading) {
        return
      }
      try {
        const response = await executeUpdateSimulation({ simulationNumber, ...updates }).unwrap()
        return response.data.simulation
      } catch (error) {
        console.error('#updateSimulationAsync: Error updating simulation', error)
        throw error
      }
    },
    [executeUpdateSimulation, isUpdateSimulationLoading],
  )

  const deleteSimulationAsync = useCallback(
    async (simulationNumber) => {
      if (isDeleteSimulationLoading) {
        return
      }
      try {
        const response = await executeDeleteSimulation(simulationNumber).unwrap()
        return response.data
      } catch (error) {
        console.error('#deleteSimulationAsync: Error deleting simulation', error)
        throw error
      }
    },
    [executeDeleteSimulation, isDeleteSimulationLoading],
  )

  const startSimulationAsync = useCallback(
    async (simulationNumber) => {
      if (isStartSimulationLoading) {
        return
      }
      try {
        const response = await executeStartSimulation(simulationNumber).unwrap()
        return response.data.simulation
      } catch (error) {
        console.error('#startSimulationAsync: Error starting simulation', error)
        throw error
      }
    },
    [executeStartSimulation, isStartSimulationLoading],
  )

  const stopSimulationAsync = useCallback(
    async (simulationNumber) => {
      if (isStopSimulationLoading) {
        return
      }
      try {
        const response = await executeStopSimulation(simulationNumber).unwrap()
        return response.data.simulation
      } catch (error) {
        console.error('#stopSimulationAsync: Error stopping simulation', error)
        throw error
      }
    },
    [executeStopSimulation, isStopSimulationLoading],
  )

  const getActiveSimulationsAsync = useCallback(
    async (params = {}) => {
      if (isGetActiveSimulationsLoading) {
        return
      }
      try {
        const response = await executeGetActiveSimulations(params).unwrap()
        return response.data.simulations
      } catch (error) {
        console.error('#getActiveSimulationsAsync: Error fetching active simulations', error)
        throw error
      }
    },
    [executeGetActiveSimulations, isGetActiveSimulationsLoading],
  )

  const getArchivedSimulationsAsync = useCallback(
    async (params = {}) => {
      if (isGetArchivedSimulationsLoading) {
        return
      }
      try {
        const response = await executeGetArchivedSimulations(params).unwrap()
        return response.data.simulations
      } catch (error) {
        console.error('#getArchivedSimulationsAsync: Error fetching archived simulations', error)
        throw error
      }
    },
    [executeGetArchivedSimulations, isGetArchivedSimulationsLoading],
  )

  return {
    createSimulationAsync,
    getSimulationAsync,
    updateSimulationAsync,
    deleteSimulationAsync,
    startSimulationAsync,
    stopSimulationAsync,
    getActiveSimulationsAsync,
    getArchivedSimulationsAsync,
    isCreateSimulationLoading,
    isGetSimulationLoading,
    isUpdateSimulationLoading,
    isDeleteSimulationLoading,
    isStartSimulationLoading,
    isStopSimulationLoading,
    isGetActiveSimulationsLoading,
    isGetArchivedSimulationsLoading,
  }
}
